import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Aviso, Botao, Cartao, useDialogo } from '../../../componentes/ui';
import {
  CHAVE_DA_PROSPECCAO,
  carregarSituacaoDoRobo,
  coletarAgora,
  rotuloDoMes,
  type ColetaResumo,
  type ExecucaoDoRobo,
  type SituacaoDoRobo,
} from '../../../servicos/prospeccao';
import { ErroDaApi } from '../../ErroDaApi';

const CHAVE_DO_ROBO = [...CHAVE_DA_PROSPECCAO, 'robo'] as const;

/** Rodando, a tela pergunta a cada 4 s; parado, a cada minuto (a automática pode começar). */
const INTERVALO_RODANDO_MS = 4000;
const INTERVALO_PARADO_MS = 60_000;

const ETAPAS: Readonly<Record<ExecucaoDoRobo['etapa'], string>> = {
  LISTA: 'Abrindo a lista pública de credenciados…',
  LOGIN: 'Entrando no portal da Receita…',
  EXTRATOS: 'Lendo os extratos',
  GRAVACAO: 'Gravando a coleta do mês…',
};

/**
 * O robô do SISCRED na Prospecção: o que ele está fazendo, como foi a última
 * rodada e o "Coletar agora".
 *
 * A rodada roda no servidor (de 1h30 a 3h): a tela só acompanha. Quando uma
 * rodada que estava em andamento termina bem, a lista recarrega e a tela vai
 * para o mês coletado.
 */
export function RoboDoSiscred(props: {
  readonly coletas: readonly ColetaResumo[];
  readonly aoConcluir: (execucao: ExecucaoDoRobo) => void;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const dialogo = useDialogo();

  const situacao = useQuery({
    queryKey: CHAVE_DO_ROBO,
    queryFn: carregarSituacaoDoRobo,
    refetchInterval: (consulta) =>
      consulta.state.data?.emAndamento ? INTERVALO_RODANDO_MS : INTERVALO_PARADO_MS,
  });

  const coletar = useMutation({
    mutationFn: coletarAgora,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CHAVE_DO_ROBO }),
  });

  // A rodada que a tela viu em andamento terminou? Bem: a lista recarrega.
  const { aoConcluir } = props;
  const acompanhada = useRef<string | null>(null);
  useEffect(() => {
    const dados = situacao.data;
    if (dados === undefined) return;
    const anterior = acompanhada.current;
    acompanhada.current = dados.emAndamento?.id ?? null;
    if (anterior === null || dados.emAndamento !== null) return;
    const terminada = dados.ultimas.find((e) => e.id === anterior);
    void queryClient.invalidateQueries({ queryKey: CHAVE_DA_PROSPECCAO });
    if (terminada?.status === 'CONCLUIDA') aoConcluir(terminada);
  }, [situacao.data, queryClient, aoConcluir]);

  if (situacao.isError) {
    return <ErroDaApi titulo="Não foi possível ver o robô do SISCRED" erro={situacao.error} />;
  }
  const s = situacao.data;
  if (s === undefined) return null;

  const pronto = s.credenciais && s.navegador;
  const agora = new Date();
  const doMes = props.coletas.find(
    (c) => c.ano === agora.getFullYear() && c.mes === agora.getMonth() + 1,
  );

  const pedirColeta = async (): Promise<void> => {
    const ok = await dialogo.confirmar({
      titulo: 'Coletar agora no SISCRED?',
      descricao:
        'O Deca entra no portal da Receita com o login configurado no servidor e lê o extrato das cerca de 1.850 empresas credenciadas, uma a uma — leva de 1h30 a 3h.\n\nPode fechar esta tela: a coleta continua no servidor e aparece aqui quando terminar.' +
        (doMes === undefined
          ? ''
          : `\n\nJá existe a coleta de ${rotuloDoMes(doMes.ano, doMes.mes)}, com ${doMes.empresas} empresas. Quando o robô terminar, a dele substitui a lista do mês. As anotações das empresas ficam.`),
      rotuloConfirmar: doMes === undefined ? 'Coletar agora' : 'Coletar e substituir',
    });
    if (ok) coletar.mutate(doMes !== undefined);
  };

  const ultima = s.ultimas[0];

  return (
    <Cartao
      variante="contorno"
      densidade="compacta"
      titulo="Robô do SISCRED"
      subtitulo={<LinhaDaAutomatica situacao={s} />}
      acao={
        s.emAndamento === null && (
          <Botao
            variante="secundario"
            tamanho="pequeno"
            disabled={!pronto}
            carregando={coletar.isPending}
            onClick={() => void pedirColeta()}
          >
            Coletar agora
          </Botao>
        )
      }
    >
      <div className="flex flex-col gap-3">
        {!pronto && (
          <Aviso tom="atencao" titulo="O robô ainda não está configurado no servidor">
            {s.credenciais
              ? 'Falta o navegador do servidor (PUPPETEER_EXECUTABLE_PATH).'
              : 'Falta o login do SISCRED — SISCRED_LOGIN e SISCRED_SENHA, que o TI cadastra no servidor.'}{' '}
            Enquanto isso, a lista do mês entra pela planilha.
          </Aviso>
        )}
        {s.emAndamento !== null ? (
          <Andamento execucao={s.emAndamento} />
        ) : (
          ultima !== undefined && <UltimaRodada execucao={ultima} />
        )}
        {coletar.isError && (
          <ErroDaApi titulo="Não foi possível começar a coleta" erro={coletar.error} />
        )}
      </div>
    </Cartao>
  );
}

function LinhaDaAutomatica(props: { readonly situacao: SituacaoDoRobo }): JSX.Element {
  const s = props.situacao;
  if (!s.automatica) {
    return <>Coleta automática desligada: por enquanto, a coleta é pelo botão.</>;
  }
  if (s.automaticaPausada) {
    return (
      <>
        Coleta automática pausada: o portal recusou o login na última tentativa. Ela volta depois de
        uma coleta pelo botão dar certo.
      </>
    );
  }
  return (
    <>
      Coleta automática no dia 10 (ou no próximo dia útil), às 14h
      {s.proximaColeta === null ? '.' : ` — a próxima em ${dataCurta(s.proximaColeta)}.`}
    </>
  );
}

function Andamento(props: { readonly execucao: ExecucaoDoRobo }): JSX.Element {
  const e = props.execucao;
  const total = e.cadsNaLista ?? 0;
  const lendo = e.etapa === 'EXTRATOS' && total > 0;
  const fracao = e.etapa === 'GRAVACAO' ? 1 : lendo ? Math.min(e.consultados / total, 1) : 0;
  const porcento = Math.round(fracao * 100);

  return (
    <div className="flex flex-col gap-2" aria-live="polite">
      <p className="text-sm font-medium text-conteudo-primario">
        {lendo
          ? `${ETAPAS.EXTRATOS}: ${formatarNumero(e.consultados)} de ${formatarNumero(total)} empresas (${porcento}%)`
          : ETAPAS[e.etapa]}
      </p>
      <div
        role="progressbar"
        aria-label="Andamento da coleta"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={porcento}
        className="h-2 overflow-hidden rounded-full bg-superficie-base"
      >
        <div
          className="h-full rounded-full bg-acento-indigo transition-[width] duration-700"
          style={{ width: `${Math.max(porcento, 2)}%` }}
        />
      </div>
      <p className="text-xs text-conteudo-sutil">
        {e.gatilho === 'AUTOMATICA' ? 'Coleta automática' : 'Pedida na tela'}, começou às{' '}
        {hora(e.iniciadaEm)}
        {e.falhas.length > 0 && ` · ${e.falhas.length} extrato(s) sem resposta até agora`}. Pode
        fechar esta tela: a coleta continua no servidor.
      </p>
    </div>
  );
}

function UltimaRodada(props: { readonly execucao: ExecucaoDoRobo }): JSX.Element {
  const e = props.execucao;
  const quando = `${dataCurta(e.iniciadaEm)} às ${hora(e.iniciadaEm)}`;

  if (e.status === 'FALHOU') {
    return (
      <Aviso tom="erro" titulo={`A coleta de ${quando} não deu certo`}>
        {e.mensagem ?? 'A rodada parou sem dizer por quê.'}
        {e.erro === 'LOGIN_RECUSADO' &&
          ' Antes de tentar de novo, confira com o TI o login cadastrado no servidor — tentativas seguidas com o login errado podem bloquear o acesso.'}
      </Aviso>
    );
  }

  return (
    <p className="text-sm text-conteudo-secundario">
      Última coleta do robô: {quando} — {formatarNumero(e.empresas ?? 0)} empresas acima do mínimo,
      de {formatarNumero(e.cadsNaLista ?? e.consultados)} extratos lidos
      {e.falhas.length > 0
        ? `; ${e.falhas.length} sem resposta (CAD ${e.falhas.slice(0, 5).join(', ')}${e.falhas.length > 5 ? '…' : ''}).`
        : '.'}
    </p>
  );
}

/** `AAAA-MM-DD` ou instante ISO → `13/10/2026`. */
function dataCurta(valor: string): string {
  const data = /^\d{4}-\d{2}-\d{2}$/.test(valor) ? new Date(`${valor}T12:00:00`) : new Date(valor);
  return data.toLocaleDateString('pt-BR');
}

function hora(instante: string): string {
  return new Date(instante).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function formatarNumero(n: number): string {
  return n.toLocaleString('pt-BR');
}
